import { isRouteErrorResponse, Links, Meta, Navigate, Outlet, Scripts, ScrollRestoration, useLocation } from 'react-router';
import { AuthGuard, GoogleAuthProvider } from './auth';

import type { Route } from './+types/root';
import './app.css';

export const meta: Route.MetaFunction = () => [
  { title: 'Meme Generator Admin' },
  { name: 'description', content: 'Admin panel for the meme generator template catalog' },
];

export const links: Route.LinksFunction = () => [{ rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' }];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function LoadingScreen() {
  return (
    <main className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground text-sm">Checking session…</p>
    </main>
  );
}

function RouteContent() {
  const { pathname } = useLocation();

  if (pathname === '/login') return <Outlet />;

  return (
    <AuthGuard fallback={<Navigate to="/login" replace />} loading={<LoadingScreen />}>
      <Outlet />
    </AuthGuard>
  );
}

export default function App() {
  return (
    <GoogleAuthProvider>
      <RouteContent />
    </GoogleAuthProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details = error.status === 404 ? 'The requested page could not be found.' : error.statusText || details;
  } else if (error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="container mx-auto p-4 pt-16">
      <h1 className="text-2xl font-bold">{message}</h1>
      <p className="mt-2 text-muted-foreground">{details}</p>
      {stack && (
        <pre className="mt-4 w-full overflow-x-auto rounded bg-muted p-4">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
